import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../features/modalSlice";
import { fetchSuggestions, clearSuggestions } from "../features/searchSlice";
import { RootState, AppDispatch } from "../app/store";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import useOutsideClick from "../hooks/useOutsideClick";
import crossIcon from "../assets/cross_icon.png";

const SkeletonLoader: React.FC = () => (
  <div className="space-y-2">
    <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded w-full animate-pulse"></div>
    <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded w-full animate-pulse"></div>
    <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded w-full animate-pulse"></div>
  </div>
);

const ModalSearchChat: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate(); // Hook for navigation
  const modalRef = useRef<HTMLDivElement | null>(null);

  // Custom hook for outside click
  useOutsideClick({
    modalRef,
    closeAction: () => dispatch(closeModal("ModalSearchChat")),
  });

  // Redux state
  const { suggestions, loading, isFetched } = useSelector(
    (state: RootState) => state.search
  );

  const [inputValue, setInputValue] = useState("");

  // Debounced API call
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (inputValue.trim()) {
        dispatch(fetchSuggestions(inputValue));
      } else {
        dispatch(clearSuggestions());
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [inputValue, dispatch]);

  const handleClear = () => {
    setInputValue("");
    dispatch(clearSuggestions());
  };

  const handleSuggestionClick = (id: string) => {
    dispatch(closeModal("ModalSearchChat")); // Close the modal
    navigate(`/chat/${id}`); // Navigate to the chat page with the specific ID
  };

  return (
    <div className="relative">
      <div className="fixed md:backdrop-blur-[auto] backdrop-blur-10 inset-0 flex justify-center items-center z-50">
        <div
          className="custom-modal-bg md:bg-none md:bg-transparent dark:bg-chatDarkBg/70 bg-lightBg/70 md:mx-auto mx-2 border-[1px] border-[#0b22332f] dark:border-[#0b2233] rounded-2xl w-[660px] relative"
          ref={modalRef}
        >
          <div className="flex flex-col space-y-3">
            <div className="flex border-b items-center border-gray-200 dark:border-gray-500">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="bg-transparent focus:outline-none w-full px-6 py-3 dark:text-white"
                placeholder="Search in Chat"
              />
              {inputValue && (
                <button onClick={handleClear} className="p-1 mr-2 dark:bg-white/10 bg-black/10 brightness-125 rounded-full">
                  <img
                    src={crossIcon}
                    alt="clear"
                    className="w-5 h-5 dark:invert-0 invert dark:opacity-100 opacity-50"
                  />
                </button>
              )}
            </div>

            <div className="h-[311px] overflow-y-auto">
              {loading ? (
                <SkeletonLoader />
              ) : suggestions.length > 0 ? (
                <ul>
                  {suggestions.map((chat) => (
                    <li
                      aria-hidden="true"
                      key={chat.id}
                      className="px-6 py-2 text-gray-600 dark:text-gray-200 hover:bg-white/30 dark:hover:bg-black/20 cursor-pointer"
                      onClick={() => handleSuggestionClick(chat.id.toString())} // Navigate using ID
                    >
                      {chat.title}
                    </li>
                  ))}
                </ul>
              ) : (
                isFetched &&
              inputValue.trim() && (
                  <p className="px-6 py-2 text-gray-500 dark:text-gray-400">
                  No results found.
                  </p>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalSearchChat;
